import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { formatTimeBasedOnTimezone } from '../../core/utils/date-time.util';
import { SharedModule } from '../../shared/shared.module';
import { MentorPanelService } from '../../core/services/mentor-panel/mentor-panel.service';
import { UpcomingSessionItem, MentorUpcomingSession } from '../../core/models/mentor-panel/mentor-panel-api-models';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../shared/confirmation-dialog/confirmation-dialog.component';
import { ToastrService } from 'ngx-toastr';



@Component({
    selector: 'app-upcoming-sessions',
    standalone: true,
    imports: [CommonModule, SharedModule],
    templateUrl: './upcoming-sessions.component.html',
    styleUrl: './upcoming-sessions.component.scss'
})
export class UpcomingSessionsComponent implements OnInit {
    private mentorPanelService = inject(MentorPanelService);
    private dialog = inject(MatDialog);
    private toastr = inject(ToastrService);
    sessions: MentorUpcomingSession[] = [];
    isLoading: boolean = true;

    constructor() { }

    ngOnInit(): void {
        this.fetchUpcomingSessions();
    }

    private fetchUpcomingSessions(): void {
        this.mentorPanelService.getUpcomingSessions().subscribe({
            next: (response) => {
                if (response.Success) {
                    this.sessions = response.Data.sessions.map(s => this.mapToUpcomingSession(s));
                }
                this.isLoading = false;
            },
            error: (err) => {
                console.error('Error fetching upcoming sessions', err);
                this.isLoading = false;
            }
        });
    }

    private mapToUpcomingSession(item: UpcomingSessionItem): MentorUpcomingSession {
        return {
            id: item.booking_id,
            studentName: item.student_name,
            initials: this.getInitials(item.student_name),
            sessionType: item.session_type,
            topic: 'Mentoring Session',
            date: item.date,
            time: this.formatTimeTo12h(item.start_time_utc),
            duration: `${item.duration_minutes} min`,
            isToday: this.isToday(item.date),
            status: this.isToday(item.date) ? 'TODAY' : 'ACCEPTED',
            type: 'PAID',
            avatarColorClass: this.getRandomAvatarColor(item.student_name),
            googleMeetLink: item.google_meet_link
        };
    }

    private getInitials(name: string): string {
        return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
    }

    private getRandomAvatarColor(name: string): string {
        const colors = ['blue-avatar', 'purple-avatar', 'orange-avatar', 'green-avatar'];
        const index = name.length % colors.length;
        return colors[index];
    }

    private formatTimeTo12h(isoString: string): string {
        return formatTimeBasedOnTimezone(isoString);
    }

    private isToday(dateStr: string): boolean {
        const today = new Date().toISOString().split('T')[0];
        return dateStr === today;
    }

    joinMeeting(session: MentorUpcomingSession) {
        if (session.googleMeetLink) {
            window.open(session.googleMeetLink, '_blank');
        } else {
            this.toastr.info('No meeting link available for this session.');
        }
    }

    markComplete(session: MentorUpcomingSession) {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: {
                title: 'Mark as Completed',
                message: `Are you sure you want to mark the session with ${session.studentName} as completed?`,
                confirmText: 'Complete',
                cancelText: 'Cancel',
                type: 'submit'
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.mentorPanelService.markBookingAsDone(session.id).subscribe({
                    next: (response) => {
                        if (response.Success) {
                            this.toastr.success(response.Message || 'Session marked as completed successfully');
                            this.fetchUpcomingSessions();
                        } else {
                            this.toastr.error(response.Message || 'Failed to mark session as complete');
                        }
                    },
                    error: (err) => {
                        this.toastr.error('An error occurred while marking the session as complete');
                        console.error('Error marking session as done', err);
                    }
                });
            }
        });
    }
}
